/**
 * Version affichée en pied d'écran + popup des nouveautés.
 *
 * Deux entrées pour la même popup, parce que le pied d'écran n'existe pas partout :
 *
 * - **desktop** : une puce discrète en bas à gauche — la zone est libre, le rail de
 *   navigation vivant à droite (D§7) ;
 * - **mobile** : le bas de l'écran appartient au dock et au tiroir (D§3), la puce y
 *   serait recouverte ou gênante — la version vit alors dans Réglages, via
 *   `createVersionRow()`, et ouvre la même popup.
 *
 * Le numéro vient de `config/changelog.ts`, donc de `package.json` : ce que le joueur
 * lit est exactement ce qui est déployé.
 */

import { APP_VERSION, VERSION_NOTES } from '@/config/changelog';
import { t } from '@/i18n';
import { createModal } from './components/overlays';
import type { ModalHandle } from './components/overlays';

export interface VersionInfoHandle {
  /** Puce « v0.2.0 » à poser en pied d'écran (desktop). */
  readonly chip: HTMLElement;
  /** Ligne « Version 0.2.0 · Nouveautés » à poser dans Réglages (mobile et desktop). */
  readonly row: HTMLElement;
  open(): void;
  destroy(): void;
}

function buildContent(modal: ModalHandle): void {
  for (const notes of VERSION_NOTES) {
    const head = document.createElement('h4');
    head.className = 'changelog__version';
    head.textContent = `v${notes.version}`;

    if (notes.version === APP_VERSION) {
      const badge = document.createElement('span');
      badge.className = 'changelog__current';
      badge.textContent = t('changelog.current');
      head.append(badge);
    }

    const list = document.createElement('ul');
    list.className = 'changelog__items';
    for (const key of notes.itemKeys) {
      const item = document.createElement('li');
      item.textContent = t(key);
      list.append(item);
    }

    modal.content.append(head, list);
  }
}

export function createVersionInfo(): VersionInfoHandle {
  const modal = createModal({ title: t('changelog.title'), icon: 'star' });
  document.body.append(modal.el);
  buildContent(modal);

  const label = t('changelog.open', { value: APP_VERSION });

  const chip = document.createElement('button');
  chip.type = 'button';
  chip.className = 'version-chip';
  chip.textContent = `v${APP_VERSION}`;
  chip.setAttribute('aria-label', label);
  chip.addEventListener('click', () => {
    modal.open();
  });

  const row = document.createElement('button');
  row.type = 'button';
  row.className = 'version-row';
  row.setAttribute('aria-label', label);

  const rowVersion = document.createElement('span');
  rowVersion.className = 'version-row__num';
  rowVersion.textContent = `v${APP_VERSION}`;

  const rowLabel = document.createElement('span');
  rowLabel.textContent = t('changelog.title');

  row.append(rowVersion, rowLabel);
  row.addEventListener('click', () => {
    modal.open();
  });

  return {
    chip,
    row,
    open() {
      modal.open();
    },
    destroy() {
      modal.destroy();
      chip.remove();
      row.remove();
    },
  };
}
